
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import oPropertyPreview from '../property/PropertyPreview.vue';
import oPromoBox from '../PromoBox.vue';
import oSearchSelect from './SearchSelect.vue';
import { EventBus } from '@/functions/eventBus';
import Format from '@/functions/format';
import { SearchListTexts } from '@/interfaces/searchTexts.interface';
import tracking from '@/functions/tracking';
import { ResponseTypes } from '@/models/propertiesList.model';

export default {
    components: {
        oPropertyPreview,
        oPromoBox,
        oSearchSelect,
    },

    data() {
        return {
            isDesktop: EventBus.isDesktop,
        };
    },

    props: {
        orderOptions: {
            type: Array as PropType<{ label: string; value: string }[]>,
            default: () => [],
        },
        texts: {
            type: Object as PropType<SearchListTexts>,
            default: () => ({
                count: '',
                noResults: '',
                searchAgentButton: '',
                zoomOutAndReset: '',
                openHouseTeaser: '',
                openHouseTeaserSignup: '',
            }),
        },
    },

    computed: {
        ...mapGetters({
            activeProperty: 'search/activeProperty',
            listIsLoading: 'search/listIsLoading',
            listResults: 'search/listResults',
            mapIsLoading: 'search/mapIsLoading',
            listIsCanceled: 'search/listIsCanceled',
            order: 'search/order',
            promo: 'search/promo',
            redirect: 'search/redirect',
            totalCount: 'search/totalCount',
            view: 'search/view',
        }),

        /**
         * Format count string.
         *
         * @return {string}
         */
        countText(): string {
            return this.texts.count.replace(
                '#resultater#',
                Format.amount(this.totalCount),
            );
        },

        /**
         * Check if more results can be fetched.
         *
         * @return {boolean}
         */
        hasMoreResults(): boolean {
            // Make sure we only look at properties in the result list
            // We do not want to promoboxes to interfere here
            const properties = this.listResults.filter(
                (item: ResponseTypes) => item.responseType === 'property',
            );
            return properties.length > 0 && this.totalCount > properties.length;
        },
    },
    beforeMount(){
        EventBus.$on('app.resize', () => {
            this.isDesktop = EventBus.isDesktop;
        });
    },
    mounted(): void {
        this.handleIntersections();
    },

    methods: {
        ...mapActions({
            fetchNextPage: 'search/fetchNextPage',
            setActiveProperty: 'search/setActiveProperty',
            setOrder: 'search/setOrder',
            resetFiltersAndZoom: 'search/resetFiltersAndZoom',
            setMapHasInteraction: 'search/setMapHasInteraction',
        }),

        /**
         * Start intersection observer.
         *
         * @return {void}
         */
        handleIntersections(): void {
            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver(
                    (entries: IntersectionObserverEntry[]) => {
                        if (
                            this.hasMoreResults &&
                            entries.some(
                                (entry: IntersectionObserverEntry) =>
                                    entry.intersectionRatio > 0,
                            )
                        ) {
                            this.fetchNextPage();
                        }
                    },
                    {
                        root: this.$refs.list as HTMLDivElement,
                        rootMargin: '0px 0px 400px 0px',
                    },
                );

                observer.observe(this.$refs.pagination as HTMLDivElement);
            }
        },

        setOrderChanged(e: string): void {
            this.setOrder(e);

            tracking.track(
                'trackFilter',
                'Filtersoegning',
                'Sortering skiftet',
                `Sortering: ${e}`,
            );
        },

        /**
         * Zoom out on map and reset filters.
         *
         * @return {void}
         */
        zoomOutAndReset(): void {
            this.resetFiltersAndZoom().then(() => {
                this.setMapHasInteraction(false);
                EventBus.$emit('search.reset');
            });
        },
    },
};
